import React, { useEffect, useCallback, useRef } from 'react';
import { connect } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Dispatch, bindActionCreators } from 'redux';
import InterestRecordList from '../../components/Records/Interest/InterestRecordList';
import * as RecordActions from '../../actions/Records/actions';
import * as UserActions from '../../actions/User/actions';
import { RootState } from '../../store/configureStore';
import Loading from '../../components/common/Loading';

interface RouteParams {
  institutionId: string;
  userId: string;
}

interface StateProps {
  institution: any;
  userListData: any[];
  interest: any;
}

interface DispatchProps {
  recordActions: typeof RecordActions;
  userActions: typeof UserActions;
}

type Props = StateProps & DispatchProps;

const InterestRecordListContainer: React.FC<Props> = ({
  institution,
  userListData,
  interest,
  recordActions,
  userActions,
}) => {
  const { institutionId, userId } = useParams<RouteParams>();
  const initializedRef = useRef(false);
  const userFetchedRef = useRef(false);

  const user = userListData.find((u: any) => u.user_id === userId);
  const uniqueId = user?.unique_id;

  useEffect(() => {
    if (userListData.length === 0 && !userFetchedRef.current && institutionId) {
      userFetchedRef.current = true;
      userActions.getUsersWithUniqueId(institutionId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [institutionId, userListData.length]);

  useEffect(() => {
    if (uniqueId && !initializedRef.current) {
      initializedRef.current = true;
      recordActions.fetchInterestRecords(uniqueId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uniqueId]);

  const handleFetch = useCallback(
    (id: string) => {
      recordActions.fetchInterestRecords(id);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const handleDelete = useCallback(
    (id: string, sequence: number) => {
      recordActions.deleteInterestRecord(id, sequence);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const handleResetFlags = useCallback(() => {
    recordActions.resetInterestFlags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!user) {
    return <Loading />;
  }

  return (
    <InterestRecordList
      institutionId={institutionId}
      institutionName={institution?.institution_name || ''}
      userId={userId}
      userName={user.user_name}
      uniqueId={user.unique_id || ''}
      records={interest.list}
      executing={interest.executing}
      loaded={interest.loaded}
      deleted={interest.deleted}
      onFetch={handleFetch}
      onDelete={handleDelete}
      onResetFlags={handleResetFlags}
    />
  );
};

function mapStateToProps(state: RootState): StateProps {
  return {
    institution: state.institution.institution,
    userListData: state.user.listData,
    interest: state.records.interest,
  };
}

function mapDispatchToProps(dispatch: Dispatch): DispatchProps {
  return {
    recordActions: bindActionCreators(RecordActions, dispatch),
    userActions: bindActionCreators(UserActions, dispatch),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(InterestRecordListContainer);
